import {
  useState,
  useEffect,
  useCallback,
  useRef,
  MutableRefObject,
} from 'react'
import {
  GoogleAutocompleteService,
  GooglePlaceResult,
  GooglePlaceSearchResults,
  GooglePlacesService,
} from 'types/location'

const updateSessionToken = (
  ref: MutableRefObject<google.maps.places.AutocompleteSessionToken | undefined>
) => (ref.current = new window.google.maps.places.AutocompleteSessionToken())

export const useConfigurePlaces = (
  boxRef: MutableRefObject<HTMLDivElement | null>
) => {
  const [Autocomplete, setAutocomplete] = useState<GoogleAutocompleteService>(
    {} as GoogleAutocompleteService
  )
  const [Places, setPlaces] = useState<GooglePlacesService>(
    {} as GooglePlacesService
  )
  const sessionRef = useRef<
    google.maps.places.AutocompleteSessionToken | undefined
  >()

  const captureClass = useCallback(() => {
    setAutocomplete((old: GoogleAutocompleteService) => {
      if (window.google.maps.places.AutocompleteService) {
        return new window.google.maps.places.AutocompleteService() as GoogleAutocompleteService
      } else {
        return old
      }
    })
    setPlaces((old: GooglePlacesService) => {
      if (window.google.maps.places.PlacesService && boxRef.current) {
        return new window.google.maps.places.PlacesService(
          boxRef.current
        ) as GooglePlacesService
      } else {
        return old
      }
    })
    updateSessionToken(sessionRef)
  }, [setAutocomplete, boxRef])

  useEffect(() => {
    if (window.google?.maps?.places) {
      captureClass()
    }
  }, [captureClass])

  const refreshToken = useCallback(() => {
    updateSessionToken(sessionRef)
  }, [])

  const getPredictions = useCallback(
    async ({ query, countryCode }): Promise<GooglePlaceSearchResults> => {
      if (Autocomplete.getPlacePredictions) {
        try {
          const results = await Autocomplete.getPlacePredictions({
            input: query,
            componentRestrictions: countryCode
              ? { country: countryCode }
              : undefined,
            sessionToken: sessionRef.current,
          })
          return results.predictions
        } catch (e) {
          throw e
        }
      } else {
        throw Error('Failed to load autocomplete data.')
      }
    },
    [Autocomplete]
  )

  const getPlace = useCallback(
    async (placeId: string): Promise<GooglePlaceSearchResults> => {
      try {
        if (Places.getDetails) {
          const place = await new Promise<GooglePlaceResult>(
            (resolve, reject) => {
              Places.getDetails(
                {
                  placeId,
                  sessionToken: sessionRef.current,
                  fields: [
                    'place_id',
                    'geometry.location',
                    'address_components',
                    'formatted_address',
                  ],
                },
                (place, status) => {
                  if (place) {
                    resolve(place as GooglePlaceResult)
                  } else {
                    reject(status)
                  }
                }
              )
            }
          )
          return place
        } else {
          throw Error('Error getting Places Service')
        }
      } catch (e) {
        throw e
      }
    },
    [Places]
  )

  return {
    getPredictions,
    refreshToken,
    getPlace,
  }
}
